// ************************************************************************************************************
// * JSON API type definitions
// ************************************************************************************************************

import Jsona from 'jsona';
export const dataFormatter = new Jsona({});

export type JsonApiDataObject<T> = {
  id: string;
  type: string;
  attributes: T;
};

export type JsonApiMetaObject = {
  total: number;
  ids: string[];
};

export type JsonApiResponseDto<T> = {
  data: JsonApiDataObject<T>[] | JsonApiDataObject<T>;
  included?: JsonApiDataObject<unknown>[];
  meta?: JsonApiMetaObject;
};

export type Response<T> = {
  values: T[];
  included?: unknown[];
  total?: number;
  ids?: string[];
};

export type SingleOrArray<T> = T | T[];

// * TPageQueryParams is a type used for paginated requests
export type TPageQueryParams = {
  // * In reality, this offset is the page number...
  // * which is actually mapped to the real offset on `formatPageQueryParams` method
  offset: number;
  // * Results per page
  limit: number;
};

// * TSortQueryParams is a type used for sorted requests
export type TSortQueryParams = string[];

// * TFilterQueryParams is a type used for filtered requests
export type TFilterQueryParams = {
  // * Name of the filter to apply
  name: string;
  // * Value of the filter. This value is not known a priori, it may be a string, number, boolean, ...
  // * Chose to type it as unknown, however it may be changed to a string or another type.
  value: unknown;
};

// * TPopulateQueryParams is a type used for populate requests
export type TPopulateQueryParams = string[];

// * TQueryParams is a type used on all * QUERY * enpoints defined in any RTK API slice.
// * This type is used to help conforming with json-api standard and will help to format queries accordingly
export type TQueryParams = {
  page?: TPageQueryParams;
  sort?: TSortQueryParams;
  filter?: TFilterQueryParams[];
  search?: string;
  searchAsFilter?: boolean;
  populate?: TPopulateQueryParams;
  withIds?: boolean; // to return an array of all ids that match the filters (all pages)
};

// ************************************************************************************************************
// * Utility methods related to JSON API standard. Mainly format ones.
// ************************************************************************************************************

// * Returns the url conforming to json-api standard
export function formatQueryParams(params?: TQueryParams, baseUrl = ''): string {
  if (!params) {
    return baseUrl;
  }

  // * queryParamsArr to build the url based on the params information. Eg.: page, sort, filter...
  const queryParamsArr = [];

  // * Format page params if necessary
  const pageParams = formatPageQueryParams(params.page);
  queryParamsArr.push(...pageParams);

  // * Format sort params if necessary
  const sortParams = formatSortQueryParams(params.sort);
  queryParamsArr.push(...sortParams);

  // * Format filter params if necessary
  const filterParams = formatFilterQueryParams(params.filter);
  queryParamsArr.push(...filterParams);

  // * Format search params if necessary
  const searchParam = formatSearchQueryParam(
    params.search,
    params.searchAsFilter
  );
  queryParamsArr.push(...searchParam);

  // * Format populate params if necessary
  const populateParam = formatPopulateQueryParam(params.populate);
  queryParamsArr.push(...populateParam);

  // * Format withIds param if necessary
  const withIdsParam = formatWithIdsQueryParam(params.withIds);
  queryParamsArr.push(...withIdsParam);

  return baseUrl + '/?' + queryParamsArr.join('&');
}

function formatPopulateQueryParam(populate?: TPopulateQueryParams): string[] {
  if (!populate) {
    return [];
  }

  return [`populate=${populate.join(',')}`];
}

function formatSearchQueryParam(search?: string, asFilter = false): string[] {
  if (!search) {
    return [];
  }

  return asFilter ? [`filter[search]=${search}`] : [`search=${search}`];
}

function formatPageQueryParams(page?: TPageQueryParams): string[] {
  if (!page) {
    return [];
  }
  const { limit, offset } = page;
  const computedOffset = limit * (offset - 1);

  return [`page[offset]=${computedOffset}`, `page[limit]=${limit}`];
}

function formatSortQueryParams(sort?: TSortQueryParams): string[] {
  if (!sort || sort.length === 0) {
    return [];
  }

  const sortParams = [`sort=${sort.join(',')}`];

  // * Eg.: ['sort=a,-b,c,-d'] if sort = ['a', '-b', 'c', '-d'].
  return sortParams;
}

function formatFilterQueryParams(filter?: TFilterQueryParams[]): string[] {
  if (!filter || filter.length === 0) {
    return [];
  }

  const filterParams: string[] = [];

  filter.forEach(({ value, name }) => {
    if (value !== undefined) {
      filterParams.push(`filter[${name}]=${value}`);
    }
  });

  return filterParams;
}

function formatWithIdsQueryParam(withIds?: boolean): string[] {
  if (!withIds) {
    return [];
  }

  return [`withIds=${withIds}`];
}

// * Enpoint request types to be used on RTK mutations
export enum Request {
  POST = 'POST',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

type RelationshipData = {
  id: string;
  type: string;
};

// * Single or multiple relationship data
export type Relationships = {
  [key: string]: {
    data: RelationshipData | RelationshipData[];
  };
};

// * Format request bodies to comply with JSON API
export function formatRequest<T>(
  payload: T & { id?: string; type: string },
  method: Request,
  url = '',
  includes: string[] = []
) {
  const body = serializeRequestBody(payload, includes);

  return { url, body, method };
}

// * Serialize request bodies to comply with JSON API
export function serializeRequestBody<T>(
  payload: SingleOrArray<T & { id?: string; type: string }>,
  includes: string[] = []
) {
  const body = dataFormatter.serialize({
    stuff: payload,
    includeNames: includes,
  });

  return body;
}

// TODO: Use TS-JSON API deserializer here ASAP...
// * This method processes every API response which comes in JSON API format
// * It transforms JsonApiResponseDto<T> into Response<T> which holds the values in a more comprehensible manner
export function parseJsonApiResponse<T>(
  response: JsonApiResponseDto<T>,
  responseMeta?: any,
  responseArgs?: any,
  useDeserializer = false
): Response<T> {
  // ! FIX ME: Type
  // * Try to use this always. Made it like this because other parts that used the prior version do not break.
  const deserializedData = ([] as T[]).concat(
    dataFormatter.deserialize(response)
  );

  const { data, included, meta } = response;

  // * Converts response data JsonApiDataObject<T> into JsonApiDataObject<T>[] if it is a single entity
  const responseData = ([] as JsonApiDataObject<T>[]).concat(data);

  // * Process response data independently of single or multiple values returned from the server
  const values = responseData.map(({ attributes, id }) => {
    return {
      id,
      ...attributes,
    };
  });

  return {
    values: useDeserializer ? deserializedData : values,
    included,
    total: meta?.total,
    ids: meta?.ids,
  };
}
